import React, { useState, useEffect } from "react";
import axios from "../utils/axiosConfig";
import { useAuth } from "../context/AuthContext";
import {
    Paper,
    Typography,
    Box,
    Tabs,
    Tab,
    CircularProgress
} from "@mui/material";
import GroupIcon from "@mui/icons-material/Group";
import BusinessIcon from "@mui/icons-material/Business";
import GavelIcon from "@mui/icons-material/Gavel";
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart";
import EqualizerIcon from "@mui/icons-material/Equalizer";
import EmailIcon from "@mui/icons-material/Email";
import SyncAltIcon from "@mui/icons-material/SyncAlt";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import LinkIcon from "@mui/icons-material/Link";

import ClubRegistrationStatus from "../components/dashboard/ClubRegistrationStatus";
import RegistrationsByGroup from "../components/dashboard/RegistrationsByGroup";
import JudgesOverview from "../components/dashboard/JudgesOverview";
import AdditionalServicesOverview from "../components/dashboard/AdditionalServicesOverview";
import ImportExportSyncPanel from "../components/dashboard/ImportExportSyncPanel";
import StatisticsPanel from "../components/dashboard/StatisticsPanel";
import BulkEmailPanel from "../components/dashboard/BulkEmailPanel";
import RegistrationExtensionsPanel from "../components/dashboard/RegistrationExtensionsPanel";
import WettkampfkartenLinksPanel from "../components/dashboard/WettkampfkartenLinksPanel";

import { handleApiError } from "../utils/apiErrorHandler";

interface TabPanelProps {
    children?: React.ReactNode;
    index: number;
    value: number;
}

function TabPanel(props: TabPanelProps) {
    const { children, value, index, ...other } = props;

    return (
        <div
            role="tabpanel"
            hidden={value !== index}
            id={`dashboard-tabpanel-${index}`}
            aria-labelledby={`dashboard-tab-${index}`}
            {...other}
        >
            {value === index && (
                <Box>
                    {children}
                </Box>
            )}
        </div>
    );
}

function a11yProps(index: number) {
    return {
        id: `dashboard-tab-${index}`,
        'aria-controls': `dashboard-tabpanel-${index}`,
    };
}

interface Competition {
    _id: string;
    name: string;
    // Other competition fields
}

interface CompetitionDashboardProps {
    competitionId: string;
}

const CompetitionDashboard: React.FC<CompetitionDashboardProps> = ({ competitionId }) => {
    const [activeTab, setActiveTab] = useState(0);
    const [competition, setCompetition] = useState<Competition | null>(null);
    const [loading, setLoading] = useState(true);
    const { userRole } = useAuth();

    // Prüfen, ob der Benutzer die erweiterten Tabs sehen darf (admin, organizer)
    const canViewAdvancedTabs = userRole === 'admin' || userRole === 'organizer';

    useEffect(() => {
        const fetchCompetition = async () => {
            try {
                setLoading(true);
                const { data } = await axios.get(`/api/competitions/${competitionId}`);
                setCompetition(data);
            } catch (error) {
                handleApiError(error, "Fehler beim Laden des Wettkampfes.");
            } finally {
                setLoading(false);
            }
        };

        if (competitionId) {
            fetchCompetition();
        }
    }, [competitionId]);

    const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
        setActiveTab(newValue);
    };

    // Wenn der Benutzer auf einem Tab ist, den er nicht sehen sollte, setze aktiven Tab auf 0
    useEffect(() => {
        if (!canViewAdvancedTabs && activeTab > 3) {
            setActiveTab(0);
        }
    }, [canViewAdvancedTabs, activeTab]);

    return (
        <Box sx={{ width: '100%' }}>
            <Typography variant="h4" gutterBottom sx={{ p: 2 }}>
                {loading ? (
                    <>Dashboard <CircularProgress size={20} sx={{ ml: 1 }} /></>
                ) : (
                    <>Dashboard {competition ? `'${competition.name}'` : ''}</>
                )}
            </Typography>

            <Paper elevation={3}>
                <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                    <Tabs 
                        value={activeTab} 
                        onChange={handleTabChange} 
                        aria-label="dashboard tabs"
                        variant="scrollable"
                        scrollButtons="auto"
                        indicatorColor="primary"
                        textColor="primary"
                    >
                        <Tab icon={<BusinessIcon />} label="Vereinsmeldungen" {...a11yProps(0)} />
                        <Tab icon={<GroupIcon />} label="Wettkampfklassen" {...a11yProps(1)} />
                        <Tab icon={<GavelIcon />} label="Kampfrichter" {...a11yProps(2)} />
                        <Tab icon={<ShoppingCartIcon />} label="Zusatzbuchungen" {...a11yProps(3)} />
                        
                        {/* Meldungsverlängerungen-Tab - nur für Admin und Organizer sichtbar */}
                        {canViewAdvancedTabs && (
                            <Tab icon={<AccessTimeIcon />} label="Meldungsverlängerungen" {...a11yProps(4)} />
                        )}
                        
                        {/* E-Mail-Tab - nur für Admin und Organizer sichtbar */}
                        {canViewAdvancedTabs && (
                            <Tab icon={<EmailIcon />} label="E-Mail" {...a11yProps(5)} />
                        )}
                        
                        {/* Statistiken-Tab - nur für Admin und Organizer sichtbar */}
                        {canViewAdvancedTabs && (
                            <Tab icon={<EqualizerIcon />} label="Statistiken" {...a11yProps(6)} />
                        )}
                        
                        {/* Ex-/Import-Tab - nur für Admin und Organizer sichtbar */}
                        {canViewAdvancedTabs && (
                            <Tab icon={<SyncAltIcon />} label="Ex-/Import" {...a11yProps(7)} />
                        )}

                        {/* Wettkampfkarten-Links-Tab - nur für Admin und Organizer sichtbar */}
                        {canViewAdvancedTabs && (
                            <Tab icon={<LinkIcon />} label="Kartenlinks" {...a11yProps(8)} />
                        )}
                    </Tabs>
                </Box>

                <TabPanel value={activeTab} index={0}>
                    <Paper elevation={1}>
                        <ClubRegistrationStatus competitionId={competitionId} />
                    </Paper>
                </TabPanel>

                <TabPanel value={activeTab} index={1}>
                    <Paper elevation={1}>
                        <RegistrationsByGroup competitionId={competitionId} competitionName={competition?.name} />
                    </Paper>
                </TabPanel>

                <TabPanel value={activeTab} index={2}>
                    <Paper elevation={1}>
                        <JudgesOverview competitionId={competitionId} />
                    </Paper>
                </TabPanel>

                <TabPanel value={activeTab} index={3}>
                    <Paper elevation={1}>
                        <AdditionalServicesOverview competitionId={competitionId} />
                    </Paper>
                </TabPanel>

                {/* Meldungsverlängerungen-Panel - nur für Admin und Organizer */}
                {canViewAdvancedTabs && (
                    <TabPanel value={activeTab} index={4}>
                        <Paper elevation={1}>
                            <RegistrationExtensionsPanel competitionId={competitionId} />
                        </Paper>
                    </TabPanel>
                )}

                {/* E-Mail-Panel - nur für Admin und Organizer */}
                {canViewAdvancedTabs && (
                    <TabPanel value={activeTab} index={5}>
                        <Paper elevation={1}>
                            <BulkEmailPanel competitionId={competitionId} />
                        </Paper>
                    </TabPanel>
                )}

                {/* Statistiken-Panel - nur für Admin und Organizer */}
                {canViewAdvancedTabs && (
                    <TabPanel value={activeTab} index={6}>
                        <Paper elevation={1}>
                            <StatisticsPanel competitionId={competitionId} />
                        </Paper>
                    </TabPanel>
                )}

                {/* Import-/Export-Panel - nur für Admin und Organizer */}
                {canViewAdvancedTabs && (
                    <TabPanel value={activeTab} index={7}>
                        <Paper elevation={1}>
                            <ImportExportSyncPanel competitionId={competitionId} competitionName={competition?.name || ''} />
                        </Paper>
                    </TabPanel>
                )}

                {/* Wettkampfkarten-Links-Panel - nur für Admin und Organizer */}
                {canViewAdvancedTabs && (
                    <TabPanel value={activeTab} index={8}>
                        <Paper elevation={1}>
                            <WettkampfkartenLinksPanel competitionId={competitionId} />
                        </Paper>
                    </TabPanel>
                )}
            </Paper>
        </Box>
    );
};

export default CompetitionDashboard;